import { memo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate } from 'react-router';
import { styled } from '@mui/material/styles';
import { alertActions, analyticsActions } from '@actions';
import { analyticsService } from '@services';
import { RoutePath } from '@constants/routes';
import { useTranslation, useVisibilityStatus } from '@hooks';
import { AnalyticsContainer } from '@features/Analytics/styles';
import AnalyticsWrapper from '@features/Analytics/AnalyticsWrapper';
import PageHeader from '@features/PageHeader';
import { VisibilityGridIcon } from 'assets/images/icons';
import VisibilityRates from './VisibilityRates';
import VisibilityTable from './VisibilityTable';
import LocationModal from './LocationModal';
import VisibilitySettingsModal from './VisibilitySettingsModal';
import { showRescanError } from './visibilityShared';

const StyledAnalyticsWrapper = styled(AnalyticsWrapper)({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
});

const DEFAULT_SORT = { column: 'visibility', dir: 'desc' };
const DEFAULT_PAGE_SIZE = 10;

// An empty locations filter means "every assigned location" on the backend.
const QUERY = { locations: [] };

const VisibilityScanner = memo(() => {
    const [sort, setSort] = useState(DEFAULT_SORT);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [resultsOpened, setResultsOpened] = useState(false);
    const [settingsTarget, setSettingsTarget] = useState(null);
    const [autoScanPendingIds, setAutoScanPendingIds] = useState([]);

    const {
        visibilitySummary,
        visibilitySummaryLoading,
        visibilitySummaryError,
        visibilityLocations,
        visibilityLocationsLoading,
        visibilityLocationsError,
        visibilityDetail,
        visibilityDetailLoading,
        visibilityDetailError,
    } = useSelector(state => state.analytics);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const visibilityStatus = useVisibilityStatus();

    // 'error' fails open — the data endpoints then surface their own errors.
    const visibilityReady = visibilityStatus === true || visibilityStatus === 'error';

    const fetchLocations = ({ column, dir, page, size }) => {
        dispatch(analyticsActions.getVisibilityLocations(QUERY, { sort: column, dir, page, size }));
    };

    // Gated on readiness so a disabled account's deep link never calls the 403 endpoints.
    useEffect(() => {
        if (!visibilityReady) return;
        dispatch(analyticsActions.getVisibilitySummary(QUERY));
        fetchLocations({ ...DEFAULT_SORT, page: 0, size: DEFAULT_PAGE_SIZE });
    }, [visibilityReady]);

    if (visibilityStatus === false) {
        return <Navigate to={RoutePath.LocationsManage} replace />;
    }

    const onSortChange = column => {
        const dir = sort.column === column && sort.dir === 'desc' ? 'asc' : 'desc';
        setSort({ column, dir });
        fetchLocations({ column, dir, page: 0, size: pageSize });
    };

    const onPageChange = page => {
        fetchLocations({ ...sort, page, size: pageSize });
    };

    const onPageSizeChange = size => {
        setPageSize(size);
        fetchLocations({ ...sort, page: 0, size });
    };

    const onRowClick = row => {
        setResultsOpened(true);
        dispatch(analyticsActions.getVisibilityLocation(row.locationId));
    };

    const onConfigureClick = row => {
        setSettingsTarget({ locationId: row.locationId, name: row.name });
    };

    const onRunScan = row => {
        analyticsService.rescanVisibilityLocation(row.locationId).then(
            () => {
                dispatch(alertActions.success(t('AnalyticsVisibility.scanStarted')));
            },
            rescanError => showRescanError(dispatch, t, rescanError),
        );
    };

    // The switch stays disabled until its request settles — overlapping toggles for one
    // location could be applied out of order by the server, desyncing UI and backend.
    const onToggleAutoScan = (row, enabled) => {
        setAutoScanPendingIds(prev => [...prev, row.locationId]);
        dispatch(analyticsActions.setVisibilityAutoScan(row.locationId, enabled))
            .catch(toggleError => {
                if (!toggleError?._serverError) {
                    dispatch(alertActions.error(t('apiErrors.something_wrong')));
                }
            })
            .finally(() => setAutoScanPendingIds(prev => prev.filter(id => id !== row.locationId)));
    };

    const onSettingsSaved = () => {
        const savedLocationId = settingsTarget?.locationId;
        setSettingsTarget(null);
        fetchLocations({ ...sort, page: visibilityLocations?.page || 0, size: pageSize });
        if (resultsOpened && visibilityDetail?.locationId === savedLocationId) {
            dispatch(analyticsActions.getVisibilityLocation(savedLocationId, visibilityDetail.scanId));
        }
    };

    return (
        <StyledAnalyticsWrapper>
            <PageHeader
                styles={{ marginBottom: '16px' }}
                icon={<VisibilityGridIcon />}
                title={t('AnalyticsVisibility.pageTitle')}
                subtitle={t('AnalyticsVisibility.pageSubtitle')}
            />
            <AnalyticsContainer id="pl-content-analytics-visibility">
                <VisibilityRates
                    summary={visibilitySummary}
                    error={visibilitySummaryError}
                    isLoading={visibilitySummaryLoading || !visibilityReady}
                />
                <VisibilityTable
                    data={visibilityLocations}
                    isLoading={visibilityLocationsLoading || !visibilityReady}
                    error={visibilityLocationsError}
                    sort={sort}
                    onSortChange={onSortChange}
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                    onRowClick={onRowClick}
                    onRunScan={onRunScan}
                    onConfigureClick={onConfigureClick}
                    onToggleAutoScan={onToggleAutoScan}
                    autoScanPendingIds={autoScanPendingIds}
                    onExport={format =>
                        analyticsService.exportVisibilityLocations(QUERY, { sort: sort.column, dir: sort.dir }, format)
                    }
                />
            </AnalyticsContainer>
            <LocationModal
                open={resultsOpened}
                onClose={() => setResultsOpened(false)}
                detail={visibilityDetail}
                isLoading={visibilityDetailLoading}
                error={visibilityDetailError}
                onConfigure={() =>
                    setSettingsTarget({ locationId: visibilityDetail?.locationId, name: visibilityDetail?.name })
                }
            />
            <VisibilitySettingsModal
                open={!!settingsTarget}
                onClose={() => setSettingsTarget(null)}
                locationId={settingsTarget?.locationId}
                locationName={settingsTarget?.name}
                onSaved={onSettingsSaved}
            />
        </StyledAnalyticsWrapper>
    );
});

VisibilityScanner.displayName = 'VisibilityScanner';

export default VisibilityScanner;
